import React, { useEffect, useState } from 'react';
import { Navbar } from '../../components/Navbar';
import { Footer } from '../../components/Footer';
import { UploadIcon, ArrowRightIcon } from 'lucide-react';
export const UploadFirstProductPage = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <div className="w-full bg-gray-50 dark:bg-[#0A1C1A] min-h-screen" data-id="element-5602">
      <Navbar scrolled={scrolled} data-id="element-5603" />
      <main className="pt-24 pb-20" data-id="element-5604">
        <div className="container mx-auto px-4 md:px-6" data-id="element-5605">
          <div className="max-w-4xl mx-auto" data-id="element-5606">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] rounded-xl p-8 md:p-12 mb-12 text-white relative overflow-hidden" data-id="element-5607">
              <div className="absolute right-0 bottom-0 opacity-10" data-id="element-5608">
                <UploadIcon className="h-64 w-64" data-id="element-5609" />
              </div>
              <div className="relative z-10" data-id="element-5610">
                <h1 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-5611">
                  How to Upload Your First Product
                </h1>
                <p className="text-xl opacity-90 max-w-3xl" data-id="element-5612">
                  Learn step-by-step how to upload your product, add required
                  details, and publish it correctly on the platform.
                </p>
              </div>
            </div>
            {/* Main content */}
            <div className="bg-white dark:bg-[#162825] rounded-xl p-8 md:p-10 shadow-sm mb-10" data-id="element-5613">
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6 text-lg leading-relaxed" data-id="element-5614">
                Uploading your first product on DGStack is simple once you
                understand the flow. Follow these steps to make sure your
                product is set up correctly and ready for buyers.
              </p>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6 text-lg leading-relaxed" data-id="element-5615">
                Start by going to your seller dashboard and clicking "Add New
                Product." This will open the product submission page.
              </p>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6 text-lg leading-relaxed" data-id="element-5616">
                Begin with the Basic Information section. Enter your product
                title and write a clear full description explaining what your
                product is and what it includes. You can also add a short
                description to summarize your product quickly.
              </p>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6 text-lg leading-relaxed" data-id="element-5617">
                Next, set your price. Choose a price that reflects the value of
                your product.
              </p>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6 text-lg leading-relaxed" data-id="element-5618">
                Move to the Categorization section. Select the correct category
                for your product. This is important because it determines where
                your product will appear and helps buyers find it through
                filters. You can also add optional details like platform,
                format, skill level, and niche to improve discoverability.
              </p>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6 text-lg leading-relaxed" data-id="element-5619">
                In the Media section, upload your thumbnail image. This is what
                buyers will see first, so make sure it looks clean and
                professional. You can also add preview images to show more
                details about your product.
              </p>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6 text-lg leading-relaxed" data-id="element-5620">
                Then upload your product file. Make sure it contains everything
                the buyer needs.
              </p>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6 text-lg leading-relaxed" data-id="element-5621">
                If you have a working demo, you can add a demo URL so buyers can
                see your product in action.
              </p>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6 text-lg leading-relaxed" data-id="element-5622">
                Before submitting, confirm the checkbox to verify that your
                product follows marketplace rules and that you have the right to
                sell it.
              </p>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6 text-lg leading-relaxed" data-id="element-5623">
                Finally, click "Add Product."
              </p>
              <div className="border-l-4 border-[#2DB489] dark:border-[#4ED1A0] pl-6 my-8 py-2" data-id="element-5624">
                <p className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9] italic" data-id="element-5625">
                  Your product will then be submitted for review. Once approved,
                  it will appear on your seller dashboard and become available
                  to buyers.
                </p>
              </div>
            </div>
            {/* Related resources */}
            <div className="bg-white dark:bg-[#162825] rounded-xl p-8 shadow-sm mb-10" data-id="element-5626">
              <h2 className="text-xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-5627">
                Related Resources
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5628">
                <a href="/learn/preparing-files" className="group block bg-gray-50 dark:bg-[#1A3330] rounded-lg p-5 transition-all hover:shadow-md" data-id="element-5629">
                  <h3 className="font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2 group-hover:text-[#2DB489] dark:group-hover:text-[#4ED1A0] transition-colors" data-id="element-5630">
                    Preparing Your Files for Upload
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-[#B0C1BD] mb-3" data-id="element-5631">
                    Organize and prepare your files so buyers can easily access
                    and use your product.
                  </p>
                  <div className="flex items-center text-[#2DB489] dark:text-[#4ED1A0] text-sm font-medium" data-id="element-5632">
                    <span data-id="element-5633">Read more</span>
                    <ArrowRightIcon className="ml-1 h-4 w-4 transform group-hover:translate-x-1 transition-transform" data-id="element-5634" />
                  </div>
                </a>
                <a href="/learn/designing-thumbnails" className="group block bg-gray-50 dark:bg-[#1A3330] rounded-lg p-5 transition-all hover:shadow-md" data-id="element-5635">
                  <h3 className="font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2 group-hover:text-[#2DB489] dark:group-hover:text-[#4ED1A0] transition-colors" data-id="element-5636">
                    Designing Thumbnails That Attract Buyers
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-[#B0C1BD] mb-3" data-id="element-5637">
                    Create clean, eye-catching thumbnails that make your product
                    stand out.
                  </p>
                  <div className="flex items-center text-[#2DB489] dark:text-[#4ED1A0] text-sm font-medium" data-id="element-5638">
                    <span data-id="element-5639">Read more</span>
                    <ArrowRightIcon className="ml-1 h-4 w-4 transform group-hover:translate-x-1 transition-transform" data-id="element-5640" />
                  </div>
                </a>
              </div>
            </div>
            {/* CTA */}
            <div className="bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] rounded-xl p-8 text-white text-center" data-id="element-5641">
              <h2 className="text-2xl font-bold mb-4" data-id="element-5642">
                Ready to upload your first product?
              </h2>
              <p className="text-lg opacity-90 mb-6 max-w-2xl mx-auto" data-id="element-5643">
                Follow these steps and get your product live on the marketplace.
              </p>
              <a href="/sell-product" className="inline-block bg-white text-[#0D3C45] px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition-all" data-id="element-5644">
                Start Selling
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-5645" />
    </div>;
};